import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router";
import {
  Menu, X, Phone, Mail, Clock, ChevronDown, MapPin, ChevronRight, ArrowRight,
  Home, Wrench, Users, BookOpen, MessageSquare, Package, FolderOpen,
  Wind, Flame, Snowflake, Zap, FlaskConical,
  type LucideIcon
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import solaceLogo from "../../assets/Solacesmartlogo.webp";
import { servicesService } from "../../lib/services/servicesService";
import * as Icons from "lucide-react";

interface NavChild {
  label: string;
  path: string;
  icon: LucideIcon;
}

interface NavLink {
  label: string;
  path: string;
  icon: LucideIcon;
  children?: NavChild[];
}

const navLinks: NavLink[] = [
  { label: "Home", path: "/", icon: Home },
  {
    label: "Services",
    path: "/services",
    icon: Wrench,
    children: [
      { label: "HVAC Projects", path: "/services/hvac", icon: Wind },
      { label: "Heat Pump Water Heater", path: "/services/heat-pump", icon: Flame },
      { label: "Refrigeration & Cold Room", path: "/services/refrigeration", icon: Snowflake },
      { label: "Electrical Panel & PLC", path: "/services/electrical", icon: Zap },
      { label: "Refrigerant ISO Tank System", path: "/services/refrigerant", icon: FlaskConical },
    ],
  },
  { label: "Products", path: "/products", icon: Package },
  { label: "Projects", path: "/projects", icon: FolderOpen },
  { label: "About", path: "/about", icon: Users },
  { label: "Blog", path: "/blog", icon: BookOpen },
  { label: "Contact", path: "/contact", icon: MessageSquare },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [dynamicNavLinks, setDynamicNavLinks] = useState<NavLink[]>(navLinks);
  const location = useLocation();

  useEffect(() => {
    servicesService.list()
      .then((data) => {
        const active = data.filter(s => s.status !== "Inactive");
        const sorted = active.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));

        setDynamicNavLinks(prev => prev.map(link => {
          if (link.label === "Services" && sorted.length > 0) {
            return {
              ...link,
              children: sorted.map(s => {
                const Icon = (Icons as any)[s.icon] || Icons.Settings;
                return {
                  label: s.shortTitle || s.title,
                  path: `/services/${s.slug}`,
                  icon: Icon as LucideIcon,
                };
              })
            };
          }
          return link;
        }));
      })
      .catch(console.error);
  }, []);

  // Handle scroll to add more blur/shadow when scrolling down
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>

      {/* Main Navbar - Glassmorphism */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 border-b ${scrolled
          ? "bg-white/95 backdrop-blur-xl shadow-md border-gray-200 py-1"
          : "bg-white/95 backdrop-blur-md shadow-sm border-[#1a2d50]/5 py-2"
          }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 flex items-center justify-between h-16 lg:h-14 transition-all duration-300">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 group">
            <img
              src={solaceLogo}
              alt="Solace Smart Solutions"
              className="h-9 md:h-11 w-auto object-contain group-hover:opacity-90 transition-opacity"
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-1.5 ml-8">
            {dynamicNavLinks.map((link, index) => (
              <div key={link.label} className="flex items-center">
                <div
                  className="relative"
                  onMouseEnter={() => link.children && setHoveredDropdown(link.label)}
                  onMouseLeave={() => setHoveredDropdown(null)}
                >
                  <Link
                    to={link.path}
                    className={`px-4 py-2.5 rounded-full transition-all duration-300 flex items-center gap-1.5 ${location.pathname === link.path
                      ? "text-[#1a2d50] bg-[#E9A319]/25 font-bold"
                      : "text-[#1a2d50]/80 hover:text-[#1a2d50] hover:bg-[#E9A319]/10 font-semibold"
                      }`}
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.9375rem" }}
                  >
                    {link.label}
                    {link.children && (
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${hoveredDropdown === link.label ? 'rotate-180 text-[#E9A319]' : 'opacity-50'}`} />
                    )}
                  </Link>

                  {/* Dropdown - Glassmorphism */}
                  {link.children && (
                    <AnimatePresence>
                      {hoveredDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 5, scale: 0.98 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] border border-[#1a2d50]/5 py-3 z-50 overflow-hidden"
                        >
                          {link.children.map((child) => (
                            <Link
                              key={child.label}
                              to={child.path}
                              className={`flex items-center gap-3.5 px-5 py-3 text-sm transition-all duration-200 group relative ${location.pathname === child.path
                                ? "text-[#1a2d50] bg-[#E9A319]/15 font-bold"
                                : "text-[#1a2d50]/70 hover:text-[#1a2d50] hover:bg-black/5 font-medium"
                                }`}
                              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${location.pathname === child.path ? "bg-[#E9A319]/20" : "bg-black/5 group-hover:bg-white shadow-sm"
                                }`}>
                                <child.icon className={`w-4 h-4 ${location.pathname === child.path ? "text-[#E9A319]" : "text-[#1a2d50]/60 group-hover:text-[#E9A319]"}`} />
                              </div>
                              <span className="group-hover:translate-x-1 transition-transform">{child.label}</span>

                              {/* Active indicator dot */}
                              {location.pathname === child.path && (
                                <div className="absolute right-5 w-1.5 h-1.5 rounded-full bg-[#E9A319]"></div>
                              )}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>

                {/* Vertical Separator */}
                {index < dynamicNavLinks.length - 1 && (
                  <span className="text-[#1a2d50]/20 font-light select-none mx-1">|</span>
                )}
              </div>
            ))}

          </div>

          {/* Contact Buttons (Desktop) + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:flex items-center gap-3">
              <a href="tel:+918800878095" className="bg-[#2c538c] text-white px-5 py-2 rounded-full flex items-center gap-2 hover:bg-[#1a2d50] transition-all active:scale-95 group/btn">
                <Phone className="w-4 h-4 animate-[bounce_2s_infinite] group-hover/btn:animate-none" />
                <span className="font-semibold text-sm tracking-wide">+91 88008-78095</span>
              </a>
              <a href="mailto:Connect@Solacesmart.com" className="bg-[#2c538c] text-white px-5 py-2 rounded-full flex items-center gap-2 hover:bg-[#1a2d50] transition-all active:scale-95 group/btn">
                <Mail className="w-4 h-4 animate-[pulse_2s_infinite] group-hover/btn:animate-none" />
                <span className="font-semibold text-sm tracking-wide">Connect@Solacesmart.com</span>
              </a>
            </div>

            <button
              className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-[#1a2d50] hover:bg-black/5 hover:text-[#E9A319] transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav >

      {/* Mobile Off-Canvas Menu - Upgraded to frosted glass */}
      <AnimatePresence>
        {
          mobileOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] lg:hidden"
                onClick={() => setMobileOpen(false)}
              />

              {/* Slide-in panel */}
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 250 }}
                className="fixed top-0 left-0 bottom-0 w-[85%] max-w-[380px] bg-white z-[70] lg:hidden flex flex-col shadow-2xl border-r border-white/20"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-[#1a2d50]/10">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="shrink-0">
                    <img src={solaceLogo} alt="Solace Smart Solutions" className="h-10 w-auto object-contain" />
                  </Link>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="w-10 h-10 rounded-full bg-black/5 flex items-center justify-center hover:bg-[#E9A319]/10 hover:text-[#E9A319] transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-5 h-5 text-[#1a2d50]/70" />
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto py-5 px-4 custom-scrollbar">
                  <nav className="space-y-1.5">
                    {dynamicNavLinks.map((link) => {
                      const isActive = location.pathname === link.path;
                      const isExpanded = expandedMobile === link.label;

                      return (
                        <div key={link.label}>
                          {link.children ? (
                            <>
                              <button
                                onClick={() => setExpandedMobile(isExpanded ? null : link.label)}
                                className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl transition-all duration-200 ${isActive || isExpanded
                                  ? "text-[#1a2d50] bg-[#E9A319]/20 shadow-sm"
                                  : "text-[#1a2d50]/80 hover:bg-black/5 hover:text-[#1a2d50]"
                                  }`}
                                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}
                              >
                                <div className="flex items-center gap-3.5">
                                  <link.icon className={`w-5 h-5 ${isActive || isExpanded ? "text-[#E9A319]" : "opacity-60"}`} />
                                  <Link
                                    to={link.path}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMobileOpen(false);
                                    }}
                                    className="text-[15px] hover:text-[#E9A319] transition-colors"
                                  >
                                    {link.label}
                                  </Link>
                                </div>
                                <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                  <ChevronDown className="w-4 h-4 opacity-50" />
                                </motion.div>
                              </button>

                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: "easeInOut" }}
                                    className="overflow-hidden"
                                  >
                                    <div className="mx-4 mt-2 px-3 py-2 space-y-1 bg-black/5 rounded-xl">
                                      {link.children.map((child) => (
                                        <Link
                                          key={child.label}
                                          to={child.path}
                                          onClick={() => setMobileOpen(false)}
                                          className={`flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all text-[14px] ${location.pathname === child.path
                                            ? "bg-white text-[#E9A319] shadow-sm font-semibold"
                                            : "text-[#1a2d50]/70 hover:text-[#1a2d50] hover:bg-white/50"
                                            }`}
                                          style={{ fontFamily: "'Inter', sans-serif" }}
                                        >
                                          <child.icon className={`w-4 h-4 ${location.pathname === child.path ? "text-[#E9A319]" : "opacity-50"}`} />
                                          {child.label}
                                        </Link>
                                      ))}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </>
                          ) : (
                            <Link
                              to={link.path}
                              onClick={() => setMobileOpen(false)}
                              className={`flex items-center gap-3.5 px-5 py-3.5 rounded-2xl transition-all duration-200 ${isActive
                                ? "text-[#1a2d50] bg-[#E9A319]/25 font-bold shadow-sm"
                                : "text-[#1a2d50]/80 hover:bg-black/5 hover:text-[#1a2d50] font-semibold"
                                }`}
                              style={{ fontFamily: "'Inter', sans-serif", fontSize: "15px" }}
                            >
                              <link.icon className={`w-5 h-5 ${isActive ? "text-[#E9A319]" : "opacity-60"}`} />
                              {link.label}
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </nav>
                </div>

                {/* Footer */}
                <div className="px-6 py-6 border-t border-[#1a2d50]/10 bg-white/50 space-y-4">
                  <Link
                    to="/contact"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#E9A319] to-[#d4920f] text-[#1a2d50] w-full py-4 rounded-xl shadow-lg shadow-[#E9A319]/25 hover:shadow-xl transition-all active:scale-[0.98]"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "0.95rem" }}
                  >
                    REQUEST A QUOTE
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <div className="flex flex-col gap-3 text-sm text-[#1a2d50]/60 font-medium">
                    <a href="tel:+918800878095" className="flex items-center gap-3 hover:text-[#E9A319] transition-colors p-2 rounded-lg hover:bg-black/5">
                      <div className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center">
                        <Phone className="w-3.5 h-3.5 text-[#1a2d50]" />
                      </div>
                      +91 88008-78095
                    </a>
                    <a href="mailto:Connect@Solacesmart.com" className="flex items-center gap-3 hover:text-[#E9A319] transition-colors p-2 rounded-lg hover:bg-black/5">
                      <div className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center">
                        <Mail className="w-3.5 h-3.5 text-[#1a2d50]" />
                      </div>
                      Connect@Solacesmart.com
                    </a>
                  </div>
                </div>
              </motion.div>
            </>
          )
        }
      </AnimatePresence >
    </>
  );
}